import { resolveIcon } from '../../styles/icons'
import styled, { css } from 'styled-components'
import { getFromLS } from '../../utils/localStore/localStore'
import useActiveElement from '../../hooks/useActiveElement'
import useScreenSize from '../../hooks/useScreenSize'
import useTranslation from '../../hooks/useTranslation'

const IconWrap = styled.div`
  display: inline-block;
//  transform: translateY(-2px);
  font-size: 1.5rem;
  margin-right: 0.5rem;

  @media (max-width: 500px) {
    font-size: 1.5rem;
  }
`

const LabelWrap = styled.span`
  @media (max-width: 500px) {
    display: none; /* Hide by default on small screens */
    font-size: 1rem;

    &.active {
      display: inline; /* Show only when active */
      opacity: 1;
      color: inherit; /* Inherit color from parent TabButton */
    }
  }
`;



const TabList = styled.ul`
    
    --tabs-border-width: 8px;
    --tabs-border-radius: 0.375rem;
    --nav-link-padding-x: 1rem;
    --nav-link-padding-y: 0.5rem;
    --nav-link-font-weight: 500 ;
    margin-bottom: 1rem!important;
    display: flex;
    flex-wrap: wrap;
    padding-left: 0;
    margin-bottom: 0;
    list-style: none;
    /* The track and the active tab's indicator are both inset shadows painted
       up from the same bottom edge, so they round to the same device pixels.
       A border on the list against a border on a negatively-margined button
       drifted apart by a pixel at fractional heights. */
    box-shadow: inset 0 calc(var(--tabs-border-width) * -1) 0 ${({ theme }) => theme.tab.borderColor};

  &.rounded {
    box-shadow: none;
    border-bottom: var(--tabs-border-width) solid ${({ theme }) => theme.tab.activeBgColor};
  }
`

const TabListItem = styled.li`
  // Stretch the button to the row so its bottom edge is the list's bottom edge
  display: flex;
`

const TabButton = styled.button`
  // Add your button styles here
    position: relative;
    background: 0 0;
    border: 0;
    border-top-left-radius: var(--tabs-border-radius);
    border-top-right-radius: var(--tabs-border-radius);
    display: block;
    padding: calc(var(--nav-link-padding-y) + var(--tabs-border-width)) var(--nav-link-padding-x);
    font-size: var(--nav-link-font-size);
    font-weight: var(--nav-link-font-weight);
    text-decoration: none;
    transition: color .15s ease-in-out,background-color .15s ease-in-out,border-color .15s ease-in-out;
    color: ${({ theme }) => theme.font.color};

&.active{
  color:${({ theme }) => theme.tab.navLinkColor};
  box-shadow: inset 0 calc(var(--tabs-border-width) * -1) 0 ${({ theme }) => theme.tab.navLinkColor};

}

&.rounded{
  color:${({ theme }) => theme.tab.navLinkColor};
  margin-bottom: calc(var(--tabs-border-width) * -1);
  border: var(--tabs-border-width) solid transparent;
  border-left: 0px;
  border-right: 0px;
  padding: var(--nav-link-padding-y) var(--nav-link-padding-x);
  box-shadow: none;
}

&.active.rounded{
  background-color: ${({ theme }) => theme.tab.activeBgColor};
  border-color: ${({ theme }) => theme.tab.activeBorderColor};
  color: ${({ theme }) => theme.tab.activeColor};
  border-bottom: 1px solid ${({ theme }) => theme.tab.borderColor};;

}

@media (hover: hover) {
  /* Only apply hover styles on devices that support hover */
  &.rounded:hover {
    isolation: isolate;
    border-color: ${({ theme }) => theme.tab.hoverBorder};
  }

  &.rounded:not(.active):hover {
    color: ${({ theme }) => theme.tab.hoverColor};
  }

  &:not(.rounded):hover {
    box-shadow: inset 0 calc(var(--tabs-border-width) * -1) 0 ${({ theme }) => theme.tab.hoverColor};
    color: ${({ theme }) => theme.tab.hoverColor};
  }

  &.rounded:hover {
    border-bottom: var(--tabs-border-width) solid ${({ theme }) => theme.tab.hoverColor};
    color: ${({ theme }) => theme.tab.hoverColor};
  }
}

${({ disabled }) =>
    disabled &&
    css`
        opacity: 0.5; // Example: Reduce opacity for disabled button
        cursor: not-allowed; // Example: Change cursor style for disabled button
        color: ${({ theme }) => theme.tab.disabledColor};
        pointer-events: none;
    
    `}
`

const TabContentWrapper = styled.div`
  // Add your tab content styles here
`

const TabContent = styled.div`
  // Add your tab content styles here
  transition: opacity .15s linear;
  display: none;

  &.active{
 display: block;
}

`


const isTabVisible = (tab, conditionValue, screenSize) => {
  if (tab.targets && tab.targets.length > 0) {
    const { isMobile, isTablet } = screenSize
    const current = isMobile ? 'mobile' : isTablet ? 'tablet' : 'desktop'
    if (!tab.targets.includes(current)) return false
  }
  if (!conditionValue) return true
  const matching = conditionValue.filter(cv => cv.tab === tab.key)
  if (matching.length === 0) return true
  return matching.every(cv => cv.result)
}

const Tabs = ({ config, id, type, conditionValue }) => {
  // Try to retrieve the current Tab
  let currentActiveElement = getFromLS(`activeTab`, id);
  if (!currentActiveElement) currentActiveElement = config[0].key
  const { t } = useTranslation()

  const { isMobile, isTablet } = useScreenSize()
  const screenSize = { isMobile, isTablet }

  //Set the current tab to be the active element
  const { activeElem, handleActiveElem } = useActiveElement(`activeTab`, currentActiveElement, id)

  // If stored tab is not visible (hidden by condition), fall back to first visible tab
  const visibleTabs = config.filter(tab => isTabVisible(tab, conditionValue, screenSize))
  const effectiveActiveElem = visibleTabs.some(t => t.key === activeElem)
    ? activeElem
    : visibleTabs[0]?.key

  return (
    <>
      <TabList className={`${type}`} role="tablist">
        {config.map((tab) => (
          isTabVisible(tab, conditionValue, screenSize)
          && (
            <TabListItem
              key={tab.key}
              role="presentation"
            >
              <TabButton
                type="button"
                id={`tab-${tab.key}`}
                role="tab"
                data-rr-ui-event-key={tab.key}
                aria-controls={`tabpane-${tab.key}`}
                aria-selected={effectiveActiveElem === tab.key}
                className={`${effectiveActiveElem === tab.key ? `active ${type}` : `${type}`}`}
                onClick={() => handleActiveElem(tab.key)}
                disabled={tab.disabled}
                data-testid={`${tab.id}`}
              >
                <IconWrap
                  className={`${activeElem === tab.key ? `active ${type}` : `${type}`}`}
                >
                  {resolveIcon(tab.icon)}
                </IconWrap>
                  <LabelWrap className={`${activeElem === tab.key ? `active ${type}` : `${type}`}`}>{t(tab.label)}</LabelWrap>
              </TabButton>
            </TabListItem>
          )
        ))}
      </TabList>

      <TabContentWrapper className="tab-content">
        {config.map((tab) => (
          isTabVisible(tab, conditionValue, screenSize)
          && <TabContent
            key={tab.key}
            role="tabpanel"
            id={`tabpane-${tab.key}`}
            aria-labelledby={`tab-${tab.key}`}
            className={`${effectiveActiveElem === tab.key ? 'active show' : ''
              }`}
          >
            {tab.content}
          </TabContent>
        ))}
      </TabContentWrapper>
    </>
  );
};

export default Tabs;
